import { bannerBodyColor } from '../lib/bannerContrast.js';

/**
 * The banner, as the phone actually draws it, on both of the app's pages.
 *
 * ## Why the preview is two previews
 *
 * An operator has one screen and it is in one mode. Their customers are split across two, and
 * the failure this whole feature guards against is invisible from exactly where the decision
 * is being made: a pale banner looks considered on a dark console and has no edge at all on a
 * light phone. Showing the same card twice, side by side, on the app's real page colours, turns
 * a rule an operator has to trust into something they can see.
 *
 * The validator still blocks — a preview is not enforcement, and an operator squinting at two
 * small cards is not a contrast check. But a refusal with a picture next to it is a refusal
 * somebody can act on, and half the refusals never happen because the picture was there first.
 *
 * ## Fidelity is the point, so the geometry is copied rather than approximated
 *
 * Every number here is the app's: 96px tall, a 68px artwork square, 14px semibold title, 12px
 * body, the same 20px radius, the same gutter. A preview that is merely *suggestive* of the
 * card would move the failure from "an operator could not tell" to "an operator was told
 * wrong", which is worse. The muted body colour comes from `bannerBodyColor`, the same
 * derivation the app runs, so the second line is the exact tone a customer gets.
 *
 * The host line is drawn whenever there is an external destination, because the app draws it:
 * a customer sees where a tap is taking them before they take it, and an operator should see
 * that their banner is going to say so.
 */

/** Fixed card height. The app's `BANNER_CARD_HEIGHT`. */
const CARD_HEIGHT = 96;
/** The artwork square. The app's `BANNER_IMAGE_SIZE`. */
const IMAGE_SIZE = 68;

/** The app's page colours, which are not the surfaces the contrast rule is stated against. */
const PAGE = { light: '#F3F4F7', dark: '#0A0D13' } as const;

export interface BannerPreviewProps {
  readonly mode: 'light' | 'dark';
  readonly title: string;
  readonly body: string;
  readonly backgroundColor: string;
  readonly textColor: string;
  /** The host the app would print under the copy, or null for an in-app destination. */
  readonly host: string | null;
  /** A URL for the artwork square, or null for a banner with none. */
  readonly imageSrc: string | null;
}

export function BannerPreview({
  mode,
  title,
  body,
  backgroundColor,
  textColor,
  host,
  imageSrc,
}: BannerPreviewProps): JSX.Element {
  const muted = bannerBodyColor(textColor, backgroundColor);

  return (
    <div className="banner-preview">
      <div className="banner-preview__label">{mode === 'light' ? 'Light mode' : 'Dark mode'}</div>
      <div className="banner-preview__page" style={{ background: PAGE[mode] }}>
        <div
          className="banner-preview__card"
          style={{ background: backgroundColor, height: CARD_HEIGHT }}
        >
          <div className="banner-preview__copy">
            <div
              className="banner-preview__title"
              style={{ color: textColor }}
              title={title}
            >
              {title === '' ? 'Your headline' : title}
            </div>
            {body !== '' && (
              <div className="banner-preview__body" style={{ color: muted }}>
                {body}
              </div>
            )}
            {host !== null && (
              <div className="banner-preview__host" style={{ color: muted }}>
                <span aria-hidden="true">↗</span> {host}
              </div>
            )}
          </div>

          {imageSrc !== null && (
            /*
             * The framed tile, copied from the app for the same reason it exists there: the
             * colours can be checked and the *bytes of an image* cannot. A logo on a white
             * plate dropped onto a pale banner has no edge and reads as a hole in the card, and
             * no rule over two hex values can catch it. A hairline in the banner's own text
             * colour at low alpha is derived from a pair already proved readable, so the tile
             * is demarcated whatever the artwork turns out to be.
             */
            <div
              className="banner-preview__thumb"
              style={{
                width: IMAGE_SIZE,
                height: IMAGE_SIZE,
                borderColor: withAlpha(textColor, 0.28),
              }}
            >
              <img src={imageSrc} alt="" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** `#RRGGBB` at an alpha, matching the app's `bannerFrameColor`. */
function withAlpha(hex: string, alpha: number): string {
  const match = /^#([0-9A-Fa-f]{6})$/.exec(hex.trim());
  if (match === null) return hex;
  const value = Number.parseInt(match[1] ?? '', 16);
  const r = (value >> 16) & 0xff;
  const g = (value >> 8) & 0xff;
  const b = value & 0xff;
  return `rgba(${String(r)},${String(g)},${String(b)},${String(alpha)})`;
}
